// Modules
import { formatISODate } from '../../../../shared/utils/date-utils';
import { Store, select } from '@ngrx/store';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
// Rxjs
import { Subscription } from 'rxjs';

// Constants & Helpers
import { APP_PATHS_CONSTANTS } from '@shared/constants';
import * as NavigationHelper from '@shared/helpers/navigation.helper';

// Store - Reducer
import { AppState } from '@store/reducers';
// Store - Actions
import * as DashboardActions from '@store/actions/dashboard.actions';
import * as GeneralActions from '@store/actions/general.actions';
// Store - Selectors
import * as DashboardSelectors from '@store/selectors/dashboard.selector';
import { IDashboardGetAutoStatisticsRequest } from '@app/general/shared/interfaces';

@Component({
    selector: 'sbi-cot-back-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  // Public variables
  public dashboardForm: FormGroup;

  // Private variables
  private _subscriptions$: Subscription[] = [];
  public minAvailableDate: Date;
  public maxAvailableDate: Date;
  // Public selectors
  public loading$ = this._store$.pipe(
    select(DashboardSelectors.selectLoadingStatus)
  );
  public dashboardStatistics$ = this._store$.pipe(
    select(DashboardSelectors.selectDashboardAutoStatistics)
  );

  // Constructor
  constructor(
    private _store$: Store<AppState>,
    private _formBuilder: FormBuilder
  ) {}

  // On component init
  ngOnInit() {
    // Build main form
    this._buildDashboardForm();
    this._defineMinimumAvailableDate();

    // Set current navigation
    this._store$.dispatch(
      GeneralActions.setCurrentPage({
        payload: NavigationHelper.getNotMenuNavigationItem(
          APP_PATHS_CONSTANTS.DASHBOARD_AUTO
        ),
      })
    );

    this.onChartsRequest();
  }

  // On component destroy
  ngOnDestroy() {
    // Reset statuses
    this._store$.dispatch(DashboardActions.reset());

    // Unsubscribe
    this._subscriptions$.forEach(sub => sub.unsubscribe());
  }

  // Public method to request data
  public onChartsRequest(): void {
    const formValue = this.dashboardForm.getRawValue();
    // Build Model Object
    const model: IDashboardGetAutoStatisticsRequest = {
      fecha_desde: formatISODate(formValue.dateFrom),
      fecha_hasta: formatISODate(formValue.dateTo),
    };

    this._store$.dispatch(
      DashboardActions.getAutoStatisticsRequested({ payload: model })
    );
  }

  // Private method to define the minimum available date
  private _defineMinimumAvailableDate(): void {
    this.minAvailableDate = null;
    this.maxAvailableDate = new Date();
  }

  private _buildDashboardForm(): void {
    // Form declaration
    const today = new Date();
    this.dashboardForm = this._formBuilder.group({
      // Dates validation
      dateFrom: [
        new Date(today.getFullYear(), today.getMonth(), 1),
        Validators.required,
      ],
      dateTo: [today, Validators.required],
    });
  }

  // Public method to get a specific form control by name
  public getFormControl(name: string): AbstractControl {
    return this.dashboardForm.get(name);
  }

  // Public method to know if the control is touched and invalid
  public isTouchedAndInvalid(name: string): boolean {
    return (
      this.getFormControl(name).touched && this.getFormControl(name).invalid
    );
  }
}
